/**
 * Marcador configurado del badge (`step.badge.marker`): construye el elemento
 * (icono / imagen / icono+texto) y lo posiciona sobre o junto al elemento ancla.
 * Puro en la parte de cálculo (`markerOffset`) para poder testearlo.
 */
import { autoUpdate } from '@floating-ui/dom';
import type { BadgeMarker, BadgeMarkerPosition } from '../types';

/**
 * Set de iconos del badge (mock 2026-09): 4 en contorno, sus versiones sólidas
 * (círculo relleno con el glifo en blanco), la mira sólida y la etiqueta «New».
 * Paths de heroicons (viewBox 24); los glifos sólidos van sin el círculo.
 */
interface BadgeIconDef {
  paths: string[];
  /** Puntos (cx, cy) dibujados como círculos rellenos (el punto de la i / !). */
  dots?: Array<[number, number]>;
  /** Círculo relleno de fondo + glifo blanco. */
  solid?: boolean;
  /** Escala del glifo alrededor del centro (para meterlo dentro del círculo). */
  scale?: number;
  /** Etiqueta de texto en vez de glifo. */
  text?: string;
}

const INFO_GLYPH =
  'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021';
const QUESTION_GLYPH =
  'M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75';
const STAR_GLYPH =
  'M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z';
const CIRCLE = 'M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
const TARGET_GLYPH =
  'M7.5 3.75H6A2.25 2.25 0 0 0 3.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0 1 20.25 6v1.5m0 9V18A2.25 2.25 0 0 1 18 20.25h-1.5m-9 0H6A2.25 2.25 0 0 1 3.75 18v-1.5M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z';

export const BADGE_ICONS: Record<string, BadgeIconDef> = {
  info: { paths: [CIRCLE, INFO_GLYPH], dots: [[12, 8.25]] },
  exclamation: { paths: [CIRCLE, 'M12 8v4.5'], dots: [[12, 15.75]] },
  question: { paths: [CIRCLE, QUESTION_GLYPH], dots: [[12, 17.25]] },
  star: { paths: [STAR_GLYPH] },
  'info-solid': { paths: [INFO_GLYPH], dots: [[12, 8.25]], solid: true, scale: 0.8 },
  'exclamation-solid': { paths: ['M12 8v4.5'], dots: [[12, 15.75]], solid: true, scale: 0.8 },
  'question-solid': { paths: [QUESTION_GLYPH], dots: [[12, 17.25]], solid: true, scale: 0.8 },
  'star-solid': { paths: [STAR_GLYPH], solid: true, scale: 0.55 },
  'target-solid': { paths: [TARGET_GLYPH], solid: true, scale: 0.6 },
  new: { paths: [], text: 'New' },
};

export const BADGE_ICON_NAMES = Object.keys(BADGE_ICONS);

export const DEFAULT_MARKER_SIZE = 14;
export const DEFAULT_MARKER_Z = 19000;
/** Separación entre el ancla y un marcador a su lado (left/right). */
const SIDE_GAP = 8;

function num(v: number | null | undefined, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function iconNode(doc: Document, name: string, size: number, color: string): Node {
  const def = BADGE_ICONS[name] ?? (BADGE_ICONS.info as BadgeIconDef);
  if (def.text) {
    const t = doc.createElement('span');
    t.textContent = def.text;
    t.style.font = `700 ${Math.round(size * 0.85)}px/1 system-ui, -apple-system, sans-serif`;
    t.style.color = color;
    return t;
  }
  const ns = 'http://www.w3.org/2000/svg';
  const svg = doc.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  const glyphColor = def.solid ? '#ffffff' : color;
  if (def.solid) {
    const bg = doc.createElementNS(ns, 'circle');
    bg.setAttribute('cx', '12');
    bg.setAttribute('cy', '12');
    bg.setAttribute('r', '12');
    bg.setAttribute('fill', color);
    svg.appendChild(bg);
  }
  const g = doc.createElementNS(ns, 'g');
  g.setAttribute('stroke', glyphColor);
  g.setAttribute('stroke-width', def.solid ? '2' : '1.8');
  g.setAttribute('stroke-linecap', 'round');
  g.setAttribute('stroke-linejoin', 'round');
  if (def.scale && def.scale !== 1) {
    const k = def.scale;
    const t = 12 - 12 * k;
    g.setAttribute('transform', `translate(${t} ${t}) scale(${k})`);
  }
  for (const d of def.paths) {
    const path = doc.createElementNS(ns, 'path');
    path.setAttribute('d', d);
    g.appendChild(path);
  }
  for (const [cx, cy] of def.dots ?? []) {
    const dot = doc.createElementNS(ns, 'circle');
    dot.setAttribute('cx', String(cx));
    dot.setAttribute('cy', String(cy));
    dot.setAttribute('r', '0.9');
    dot.setAttribute('fill', glyphColor);
    dot.setAttribute('stroke', 'none');
    g.appendChild(dot);
  }
  svg.appendChild(g);
  return svg;
}

/** Construye el elemento visual del marcador según su tipo. Sin innerHTML. */
export function buildMarkerElement(doc: Document, marker: BadgeMarker): HTMLElement {
  const size = num(marker.size, DEFAULT_MARKER_SIZE);
  const color = marker.color || '#000000';
  const el = doc.createElement('span');
  el.className = `veo-badge-marker veo-badge-marker-${marker.kind}`;
  el.style.display = 'inline-flex';
  el.style.alignItems = 'center';
  el.style.justifyContent = 'center';
  el.style.boxSizing = 'border-box';
  el.style.lineHeight = '1';

  if (marker.kind === 'image') {
    const img = doc.createElement('img');
    img.alt = '';
    img.src = marker.imageUrl || '';
    img.style.width = `${size}px`;
    img.style.height = `${size}px`;
    img.style.objectFit = 'contain';
    img.style.display = 'block';
    el.appendChild(img);
    return el;
  }

  if (marker.kind === 'icon-text') {
    el.style.gap = '6px';
    el.style.color = color;
    el.style.fontSize = `${size}px`;
    el.style.fontFamily = 'inherit';
    el.style.fontWeight = '600';
    el.style.whiteSpace = 'nowrap';
    el.style.background = marker.bgColor || 'transparent';
    const bw = num(marker.borderWidth, 0);
    if (bw > 0) el.style.border = `${bw}px solid ${marker.borderColor || color}`;
    el.style.padding = `${num(marker.paddingY, 6)}px ${num(marker.paddingX, 10)}px`;
    el.style.borderRadius = `${num(marker.radius, 999)}px`;
    if (marker.showIcon !== false)
      el.appendChild(iconNode(doc, marker.icon || 'star', size, color));
    const text = doc.createElement('span');
    text.textContent = marker.text || '';
    el.appendChild(text);
    return el;
  }

  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.appendChild(iconNode(doc, marker.icon || 'info', size, color));
  return el;
}

/**
 * Posición (esquina superior izquierda) del marcador en coordenadas de PÁGINA
 * a partir del rect del ancla. Esquinas: el marcador queda CENTRADO sobre la
 * esquina (tipo punto de notificación). left/right: al lado, centrado
 * verticalmente. center: centrado sobre el elemento.
 */
export function markerOffset(
  anchor: { left: number; top: number; width: number; height: number },
  marker: { width: number; height: number },
  position: BadgeMarkerPosition,
  offset: { x: number; y: number },
): { x: number; y: number } {
  const right = anchor.left + anchor.width;
  const bottom = anchor.top + anchor.height;
  const midY = anchor.top + anchor.height / 2 - marker.height / 2;
  const midX = anchor.left + anchor.width / 2 - marker.width / 2;
  let x: number;
  let y: number;
  switch (position) {
    case 'top-left':
      x = anchor.left - marker.width / 2;
      y = anchor.top - marker.height / 2;
      break;
    case 'bottom-left':
      x = anchor.left - marker.width / 2;
      y = bottom - marker.height / 2;
      break;
    case 'bottom-right':
      x = right - marker.width / 2;
      y = bottom - marker.height / 2;
      break;
    case 'left':
      x = anchor.left - marker.width - SIDE_GAP;
      y = midY;
      break;
    case 'right':
      x = right + SIDE_GAP;
      y = midY;
      break;
    case 'center':
      x = midX;
      y = midY;
      break;
    default: // top-right
      x = right - marker.width / 2;
      y = anchor.top - marker.height / 2;
  }
  return { x: x + offset.x, y: y + offset.y };
}

/**
 * Ancla `host` (position:absolute en <body>) al elemento; sigue scroll/resize/
 * cambios de layout con `autoUpdate`. Devuelve la función que deja de seguir.
 */
export function attachMarker(anchor: Element, host: HTMLElement, marker: BadgeMarker): () => void {
  host.style.position = 'absolute';
  host.style.zIndex = String(num(marker.zIndex, DEFAULT_MARKER_Z));
  host.style.margin = '0';
  const update = (): void => {
    const a = anchor.getBoundingClientRect();
    const m = host.getBoundingClientRect();
    const { x, y } = markerOffset(
      {
        left: a.left + window.scrollX,
        top: a.top + window.scrollY,
        width: a.width,
        height: a.height,
      },
      { width: m.width, height: m.height },
      marker.position ?? 'top-right',
      { x: num(marker.offsetX, 0), y: num(marker.offsetY, 0) },
    );
    host.style.left = `${Math.round(x)}px`;
    host.style.top = `${Math.round(y)}px`;
  };
  update();
  return autoUpdate(anchor, host, update);
}
